import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../../components/ui/tooltip';
import { 
  Loader2, Info, Volume2, Type, Trash2, Save
} from 'lucide-react';
import ReferencesSection from '../../components/ReferencesSection';
import { toast } from 'sonner';
import axios from 'axios';
import StatsSummaryBar from '../../components/StatsSummaryBar';
import IPA_TOOLTIPS from '../../data/ipaTooltips';
import {
  PULMONIC_CONSONANTS,
  PULMONIC_CONSONANTS_BASIC,
  ADDITIONAL_BASIC_CONSONANTS,
  NON_PULMONIC_CONSONANTS,
  VOWELS,
  VOWELS_BASIC,
  PLACES_OF_ARTICULATION,
  PLACES_BASIC,
  MANNERS_OF_ARTICULATION,
  MANNERS_BASIC,
  VOWEL_HEIGHTS,
  VOWEL_HEIGHTS_BASIC,
  VOWEL_BACKNESS,
  getConsonantAt,
  getVowelAt,
  PLACE_LABELS,
  MANNER_LABELS,
  HEIGHT_LABELS,
  BACKNESS_LABELS
} from '../../data/ipaData';

import { API_BASE } from '../../config/api';
const API = `${API_BASE}`;

const PhonologyPage = () => {
  const { conlang, fetchConlang , canEdit} = useOutletContext();
  const { conlangId } = useParams();
  
  const [advancedMode, setAdvancedMode] = useState(false);
  const [selectedPhonemes, setSelectedPhonemes] = useState(new Set());
  const [savedPhonemes, setSavedPhonemes] = useState([]); // Full phoneme data for phonotactics
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('consonants');

  // Fetch existing phonemes
  useEffect(() => {
    fetchPhonemes();
  }, [conlangId]);

  const fetchPhonemes = async () => {
    try {
      const response = await axios.get(`${API}/conlangs/${conlangId}/phonemes`, {
        withCredentials: true
      });
      const symbols = new Set(response.data.map(p => p.symbol));
      setSelectedPhonemes(symbols);
      setSavedPhonemes(response.data);
      setSelectedPhonemes(symbols);
    } catch (error) {
      toast.error('Failed to load phonemes');
    } finally {
      setLoading(false);
    }
  };

  const togglePhoneme = (phonemeData) => {
    if (!canEdit) return;
    const newSelected = new Set(selectedPhonemes);
    if (newSelected.has(phonemeData.symbol)) {
      newSelected.delete(phonemeData.symbol);
    } else {
      newSelected.add(phonemeData.symbol);
    }
    setSelectedPhonemes(newSelected);
  };

  const savePhonemes = async () => {
    setSaving(true);
    try {
      // Delete all existing phonemes first
      await axios.delete(`${API}/conlangs/${conlangId}/phonemes`, {
        withCredentials: true
      });

      // Prepare phonemes to save
      const phonemesToSave = [];
      
      // Add consonants
      const allConsonants = [...PULMONIC_CONSONANTS, ...ADDITIONAL_BASIC_CONSONANTS];
      allConsonants.forEach(c => {
        if (selectedPhonemes.has(c.symbol)) {
          phonemesToSave.push({
            symbol: c.symbol,
            type: 'consonant',
            category: 'pulmonic',
            manner: c.manner,
            place: c.place,
            voiced: c.voiced
          });
        }
      });

      // Add non-pulmonic consonants
      Object.entries(NON_PULMONIC_CONSONANTS).forEach(([category, consonants]) => {
        consonants.forEach(c => {
          if (selectedPhonemes.has(c.symbol)) {
            phonemesToSave.push({
              symbol: c.symbol,
              type: 'consonant',
              category: 'non-pulmonic',
              subtype: c.subtype,
              place: c.place,
              voiced: c.voiced
            });
          }
        });
      });

      // Add vowels
      VOWELS.forEach(v => {
        if (selectedPhonemes.has(v.symbol)) {
          phonemesToSave.push({
            symbol: v.symbol,
            type: 'vowel',
            category: 'vowel',
            height: v.height,
            backness: v.backness,
            rounded: v.rounded
          });
        }
      });

      // Save all phonemes
      if (phonemesToSave.length > 0) {
        await axios.post(`${API}/conlangs/${conlangId}/phonemes/bulk`, 
          { phonemes: phonemesToSave },
          { withCredentials: true }
        );
      }

      toast.success(`Saved ${phonemesToSave.length} phonemes`);
      fetchConlang();
    } catch (error) {
      toast.error('Failed to save phonemes');
    } finally {
      setSaving(false);
    }
  };

  const clearAll = async () => {
    setSelectedPhonemes(new Set());
  };

  const places = advancedMode ? PLACES_OF_ARTICULATION : PLACES_BASIC;
  const manners = advancedMode ? MANNERS_OF_ARTICULATION : MANNERS_BASIC;
  const heights = advancedMode ? VOWEL_HEIGHTS : VOWEL_HEIGHTS_BASIC;
  const consonants = advancedMode ? PULMONIC_CONSONANTS : [...PULMONIC_CONSONANTS_BASIC, ...ADDITIONAL_BASIC_CONSONANTS];
  const vowels = advancedMode ? VOWELS : VOWELS_BASIC;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
    <div className="max-w-6xl space-y-6">
      {/* Stats Summary Bar */}
      <StatsSummaryBar conlang={conlang} conlangId={conlangId} currentSection="phonology" />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-3xl font-bold text-foreground" data-testid="phonology-title">
            Phonology
          </h2>
          <p className="text-foreground-muted mt-1">
            Define the sound inventory of {conlang?.name}
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Switch 
              id="advanced-mode" 
              checked={advancedMode}
              onCheckedChange={setAdvancedMode}
              data-testid="advanced-mode-toggle"
            />
            <Label htmlFor="advanced-mode" className="text-foreground-muted text-sm">
              Advanced Mode
            </Label>
          </div>
          
          {canEdit && (
          <Button 
            onClick={savePhonemes}
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            data-testid="save-phonemes-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save
          </Button>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      <div className="flex items-center gap-6 p-4 bg-background-paper border border-border rounded-md">
        <div className="flex items-center gap-2">
          <Type className="w-4 h-4 text-secondary" />
          <span className="text-foreground-muted text-sm">Consonants:</span>
          <span className="font-mono text-secondary font-semibold" data-testid="consonant-count">
            {[...selectedPhonemes].filter(s => 
              [...PULMONIC_CONSONANTS, ...ADDITIONAL_BASIC_CONSONANTS, 
               ...NON_PULMONIC_CONSONANTS.clicks,
               ...NON_PULMONIC_CONSONANTS.implosives,
               ...NON_PULMONIC_CONSONANTS.ejectives
              ].some(c => c.symbol === s)
            ).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Volume2 className="w-4 h-4 text-primary" />
          <span className="text-foreground-muted text-sm">Vowels:</span>
          <span className="font-mono text-primary font-semibold" data-testid="vowel-count">
            {[...selectedPhonemes].filter(s => VOWELS.some(v => v.symbol === s)).length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-foreground-muted text-sm">Total:</span>
          <span className="font-mono text-foreground font-semibold" data-testid="total-count">
            {selectedPhonemes.size}
          </span>
        </div>
        <div className="flex-1" />
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={clearAll}
          className="text-destructive hover:text-destructive"
          data-testid="clear-all-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Clear All
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-background-paper border border-border">
          <TabsTrigger value="consonants" data-testid="tab-consonants">
            Consonants
          </TabsTrigger>
          <TabsTrigger value="vowels" data-testid="tab-vowels">
            Vowels
          </TabsTrigger>
          {advancedMode && (
            <TabsTrigger value="non-pulmonic" data-testid="tab-non-pulmonic">
              Non-Pulmonic
            </TabsTrigger>
          )}
        </TabsList>

        {/* Consonants Tab */}
        <TabsContent value="consonants" className="mt-4">
          <Card className="bg-background-paper border-border overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-foreground flex items-center gap-2">
                <Type className="w-5 h-5 text-secondary" />
                Pulmonic Consonants
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="consonant-chart">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="p-2 text-left text-foreground-muted font-medium sticky left-0 bg-background-paper z-10"></th>
                      {places.map(place => (
                        <th 
                          key={place} 
                          className="p-2 text-center text-foreground-muted font-medium min-w-[80px]"
                          colSpan={2}
                        >
                          {PLACE_LABELS[place]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {manners.map(manner => (
                      <tr key={manner} className="border-b border-border/50 hover:bg-accent/30">
                        <td className="p-2 text-foreground-muted font-medium sticky left-0 bg-background-paper z-10 whitespace-nowrap">
                          {MANNER_LABELS[manner]}
                        </td>
                        {places.map(place => {
                          const voiceless = getConsonantAt(consonants, place, manner, false);
                          const voiced = getConsonantAt(consonants, place, manner, true);
                          
                          return (
                            <React.Fragment key={`${place}-${manner}`}>
                              <td className="p-1 text-center border-l border-border/30">
                                {voiceless ? (
                                  <PhonemeButton
                                    phoneme={voiceless}
                                    isSelected={selectedPhonemes.has(voiceless.symbol)}
                                    onClick={() => togglePhoneme(voiceless)}
                                    isAdvanced={advancedMode}
                                  />
                                ) : (
                                  <span className="text-foreground-subtle/30">-</span>
                                )}
                              </td>
                              <td className="p-1 text-center">
                                {voiced ? (
                                  <PhonemeButton
                                    phoneme={voiced}
                                    isSelected={selectedPhonemes.has(voiced.symbol)}
                                    onClick={() => togglePhoneme(voiced)}
                                    isAdvanced={advancedMode}
                                  />
                                ) : (
                                  <span className="text-foreground-subtle/30">-</span>
                                )}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="p-4 border-t border-border bg-background-surface/50 flex items-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-secondary/20 border border-secondary" />
                  <span className="text-foreground-muted">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-muted">Left cell = voiceless</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-muted">Right cell = voiced</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-4 p-4 rounded-md bg-secondary/10 border border-secondary/30 flex gap-3">
            <Info className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
            <div className="text-secondary text-sm">
              <strong>Tip:</strong> Click on a phoneme to add it to your inventory. 
              {!advancedMode && " Enable Advanced Mode to see the full IPA chart including retroflex, uvular, and pharyngeal consonants."}
            </div>
          </div>
        </TabsContent>

        {/* Vowels Tab */}
        <TabsContent value="vowels" className="mt-4">
          <Card className="bg-background-paper border-border overflow-hidden">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg text-foreground flex items-center gap-2">
                <Volume2 className="w-5 h-5 text-primary" />
                Vowels
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="vowel-chart">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="p-2 text-left text-foreground-muted font-medium sticky left-0 bg-background-paper z-10"></th>
                      {VOWEL_BACKNESS.map(backness => (
                        <th 
                          key={backness} 
                          className="p-2 text-center text-foreground-muted font-medium min-w-[120px]"
                          colSpan={2}
                        >
                          {BACKNESS_LABELS[backness]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {heights.map(height => (
                      <tr key={height} className="border-b border-border/50 hover:bg-accent/30">
                        <td className="p-2 text-foreground-muted font-medium sticky left-0 bg-background-paper z-10 whitespace-nowrap">
                          {HEIGHT_LABELS[height]}
                        </td>
                        {VOWEL_BACKNESS.map(backness => {
                          const unrounded = getVowelAt(vowels, height, backness, false);
                          const rounded = getVowelAt(vowels, height, backness, true);
                          
                          return (
                            <React.Fragment key={`${height}-${backness}`}>
                              <td className="p-1 text-center border-l border-border/30">
                                {unrounded ? (
                                  <PhonemeButton
                                    phoneme={unrounded}
                                    isSelected={selectedPhonemes.has(unrounded.symbol)}
                                    onClick={() => togglePhoneme(unrounded)}
                                    isVowel
                                    isAdvanced={advancedMode}
                                  />
                                ) : (
                                  <span className="text-foreground-subtle/30">-</span>
                                )}
                              </td>
                              <td className="p-1 text-center">
                                {rounded ? (
                                  <PhonemeButton
                                    phoneme={rounded}
                                    isSelected={selectedPhonemes.has(rounded.symbol)}
                                    onClick={() => togglePhoneme(rounded)}
                                    isVowel
                                    isAdvanced={advancedMode}
                                  />
                                ) : (
                                  <span className="text-foreground-subtle/30">-</span>
                                )}
                              </td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Legend */}
              <div className="p-4 border-t border-border bg-background-surface/50 flex items-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded bg-primary/20 border border-primary" />
                  <span className="text-foreground-muted">Selected</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-muted">Left cell = unrounded</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-foreground-muted">Right cell = rounded</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info Box */}
          <div className="mt-4 p-4 rounded-md bg-primary/10 border border-primary/30 flex gap-3">
            <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div className="text-primary text-sm">
              <strong>Vowel Chart:</strong> Rows represent tongue height (close to open), 
              columns represent tongue position (front to back). 
              {!advancedMode && " Enable Advanced Mode to see near-close and mid vowels."}
            </div>
          </div>
        </TabsContent>

        {/* Non-Pulmonic Tab (Advanced Only) */}
        {advancedMode && (
          <TabsContent value="non-pulmonic" className="mt-4 space-y-4">
            {/* Clicks */}
            <Card className="bg-background-paper border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-foreground">
                  Clicks
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="clicks-chart">
                  {NON_PULMONIC_CONSONANTS.clicks.map(c => (
                    <PhonemeButton
                      key={c.symbol}
                      phoneme={c}
                      isSelected={selectedPhonemes.has(c.symbol)}
                      onClick={() => togglePhoneme(c)}
                      showLabel
                      isAdvanced={advancedMode}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Implosives */}
            <Card className="bg-background-paper border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-foreground">
                  Implosives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="implosives-chart">
                  {NON_PULMONIC_CONSONANTS.implosives.map(c => (
                    <PhonemeButton
                      key={c.symbol}
                      phoneme={c}
                      isSelected={selectedPhonemes.has(c.symbol)}
                      onClick={() => togglePhoneme(c)}
                      showLabel
                      isAdvanced={advancedMode}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Ejectives */}
            <Card className="bg-background-paper border-border">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg text-foreground">
                  Ejectives
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2" data-testid="ejectives-chart">
                  {NON_PULMONIC_CONSONANTS.ejectives.map(c => (
                    <PhonemeButton
                      key={c.symbol}
                      phoneme={c}
                      isSelected={selectedPhonemes.has(c.symbol)}
                      onClick={() => togglePhoneme(c)}
                      showLabel
                      isAdvanced={advancedMode}
                    />
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Info Box */}
            <div className="p-4 rounded-md bg-tertiary/10 border border-tertiary/30 flex gap-3">
              <Info className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5" />
              <div className="text-tertiary text-sm">
                <strong>Non-Pulmonic Consonants:</strong> These sounds are produced without 
                airflow from the lungs. Clicks use an ingressive velaric airstream, implosives 
                use a glottalic ingressive, and ejectives use a glottalic egressive airstream.
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Selected Phonemes Summary */}
      {selectedPhonemes.size > 0 && (
        <Card className="bg-background-paper border-border">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-foreground">
              Selected Inventory
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 font-conlang text-lg" data-testid="selected-inventory">
              {[...selectedPhonemes].sort().map(symbol => (
                <span 
                  key={symbol}
                  className="px-2 py-1 bg-accent rounded text-foreground"
                >
                  {symbol}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      <ReferencesSection section="phonology" />
    </div>
    </TooltipProvider>
  );
};

// Phoneme Button Component with enhanced tooltips
const PhonemeButton = ({ phoneme, isSelected, onClick, isVowel = false, showLabel = false, isAdvanced = false }) => {
  const baseColor = isVowel ? 'primary' : 'secondary';
  const tip = IPA_TOOLTIPS[phoneme.symbol];

  const tooltipContent = tip ? (
    isAdvanced ? (
      <span className="text-xs">{tip.description}</span>
    ) : (
      tip.example.bold ? (
        <span className="text-xs">
          as in <strong>&ldquo;{tip.example.word}&rdquo;</strong>
        </span>
      ) : (
        <span className="text-xs italic">{tip.example.word}</span>
      )
    )
  ) : null;

  const btn = (
    <button
      onClick={onClick}
      className={`
        min-w-[36px] h-9 px-2 font-conlang text-lg rounded transition-all duration-150
        ${isSelected 
          ? `bg-${baseColor}/20 border-2 border-${baseColor} text-${baseColor}` 
          : 'bg-accent/30 border border-border text-foreground hover:border-foreground-muted hover:bg-accent/50'
        }
        ${showLabel ? 'flex items-center gap-2' : ''}
      `}
      data-testid={`phoneme-${phoneme.symbol}`}
    >
      {phoneme.symbol}
      {showLabel && phoneme.place && (
        <span className="text-xs text-foreground-muted">{phoneme.place}</span>
      )}
    </button>
  );

  if (!tooltipContent) return btn;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {btn}
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-background-paper border border-border text-foreground shadow-lg px-3 py-2"
      >
        <div className="flex items-center gap-1.5">
          <span className="font-conlang text-sm font-semibold text-foreground">/{phoneme.symbol}/</span>
          <span className="text-foreground-muted">—</span>
          {tooltipContent}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

export default PhonologyPage;
